import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 
import { certiplate_logo } from '@app/shared/constants/application-constants';

export interface ExportTableOptions {
  columns?: string[];
}


@Injectable({
  providedIn: 'root'
})
export class ExportService {

  constructor() { }
  fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  fileExtension = '.xlsx';

  public exportExcel(jsonData: any[], fileName: string, options: ExportTableOptions = {}): void {
    let processedData: any[];
    const exportColumns = this.normalizeExportColumns(options.columns);

    if (exportColumns.length > 0) {
      processedData = jsonData.map((item, index) => this.projectRowForExport(item, exportColumns, index));
    } else if (fileName.startsWith('Questions_')) {
      processedData = this.processQuestionsForExcel(jsonData);
    } else {
      processedData = jsonData.map(item => {
        const sanitizedItem = this.sanitizeObjectForExcel(item);
        const customAttrs = Array.isArray(item.custom_attrs)
          ? item.custom_attrs
              .map(attr => {
                if (!attr || typeof attr !== 'object') {
                  return this.sanitizeExcelValue(attr);
                }

                const key = this.sanitizeExcelValue(attr.key ?? attr.label ?? '');
                const value = this.sanitizeExcelValue(attr.value ?? '');
                return key ? `${key}: ${value}`.trim() : value;
              })
              .filter((value) => value !== '')
              .join(', ')
          : '';

        return {
          ...sanitizedItem,
          custom_attrs: customAttrs
        };
      });
    }

    // processed data to check columns
    if (processedData.length > 0) {
      // console.log(Object.keys(processedData[0]));
    } else {
      console.log("No data available to export.");
    }

    try {
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(processedData);
      const wb: XLSX.WorkBook = { Sheets: { 'data': ws }, SheetNames: ['data'] };
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      this.saveExcelFile(excelBuffer, fileName);
    } catch (err) {
      console.error("Error during Excel export:", err);
    }
  }

  public exportQuestionsTableToPDF(
    jsonData: any[],
    fileName: string,
    courseMap: Map<number, string>,
    orgLogoUrl?: string,
    options: ExportTableOptions = {}
  ): void {
  const exportColumns = this.normalizeExportColumns(options.columns);
  const hasManyColumns = exportColumns.length > 7;
  const doc = new jsPDF(hasManyColumns ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = hasManyColumns ? 6 : 10;
  const generatedOn = new Date().toLocaleString();
  const startY = 16;

  const drawPageBranding = (currentPage: number, totalPages: number) => {

    if (currentPage === 1) {

      const headerY = 6;
      doc.setFontSize(14);
      doc.setTextColor(52, 73, 94);
      doc.setFont('helvetica', 'bold');
      doc.text('Questions List', margin, headerY, { baseline: 'middle' });

      let rightX = pageWidth - margin;

      // ORG LOGO
      if (orgLogoUrl) {
        try {
          const orgLogoMaxWidth = 40;
          const orgLogoMaxHeight = 16;

          const orgLogoProps = doc.getImageProperties(orgLogoUrl);
          const orgLogoRatio = orgLogoProps.width / orgLogoProps.height;

          let orgLogoWidth = orgLogoMaxWidth;
          let orgLogoHeight = orgLogoWidth / orgLogoRatio;

          if (orgLogoHeight > orgLogoMaxHeight) {
            orgLogoHeight = orgLogoMaxHeight;
            orgLogoWidth = orgLogoHeight * orgLogoRatio;
          }

          const orgLogoY = headerY - (orgLogoHeight / 2); // ✅ vertical align

          doc.addImage(
            orgLogoUrl,
            'PNG',
            rightX - orgLogoWidth - 5,
            orgLogoY,
            orgLogoWidth,
            orgLogoHeight
          );

          rightX -= (orgLogoWidth + 10);
        } catch (e) {
          console.error('Organization logo error:', e);
        }
      }
      try {
        const logoMaxWidth = 56;
        const logoMaxHeight = 18;
        const logoProps = doc.getImageProperties(certiplate_logo);
        const logoRatio = logoProps.width / logoProps.height;
        let logoWidth = logoMaxWidth;
        let logoHeight = logoWidth / logoRatio;
        if (logoHeight > logoMaxHeight) {
          logoHeight = logoMaxHeight;
          logoWidth = logoHeight * logoRatio;
        }
        const logoY = headerY - (logoHeight / 2);
        doc.addImage(
          certiplate_logo,
          'PNG',
          rightX - logoWidth,
          logoY,
          logoWidth,
          logoHeight
        );
      } catch (e) {
        console.error('Certiplate logo error:', e);
      }

      doc.setDrawColor(46, 139, 87);
      doc.setLineWidth(1);
      doc.line(margin, 12, pageWidth - margin, 12);
    }
    doc.setDrawColor(46, 139, 87);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);
    doc.setFontSize(8);
    doc.setTextColor(52, 73, 94);
    doc.setFont('helvetica', 'normal');
    doc.text(`Downloaded on: ${generatedOn}`, margin, pageHeight - 6);
    doc.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, pageHeight - 6, {
      align: 'right',
    });
    doc.text('Powered by Certiplate', pageWidth / 2, pageHeight - 6, {
      align: 'center',
    });
  };

  const tableColumn = exportColumns.length > 0 ? exportColumns : [
    "S. No.",
    "Question Text",
    "Course/Job Role",
    "Version",
    "Evaluation Mode",
    "Type",
    "Difficulty Level",
    "Status",
  ];

  const tableRows: any[] = [];

  jsonData.sort((a, b) => {
    const nameA = courseMap.get(a.course_id) || '';
    const nameB = courseMap.get(b.course_id) || '';
    return nameA.localeCompare(nameB);
  });

  jsonData.forEach((item, index) => {
    if (exportColumns.length > 0) {
      tableRows.push(exportColumns.map((column) => this.resolveQuestionExportValue(item, column, index, courseMap)));
      return;
    }

    const row = [
      index + 1,
      this.stripHtmlTags(item.question_text) || 'null',
      courseMap.get(item.course_id) || 'null',
      item.version || 'null',
      item.question_category ?? 'null',
      item.question_type_name ?? 'null',
      item.difficulty_level_name ?? 'null',
      item.is_active ? 'Active' : 'Inactive',
    ];

    tableRows.push(row);
  });

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY,
    styles: {
      fontSize: hasManyColumns ? 5.5 : 7,
      overflow: 'linebreak',
      halign: 'left',
      valign: 'middle',
      cellPadding: hasManyColumns ? 1 : 2,
      lineColor: [220, 220, 220],
      lineWidth: 0.1
    },
    columnStyles: exportColumns.length > 0
      ? this.buildDynamicPdfColumnStyles(tableColumn, pageWidth, margin)
      : {
        0: { cellWidth: 10 },
        1: { cellWidth: hasManyColumns ? 55 : 50 },
        2: { cellWidth: hasManyColumns ? 30 : 25 },
        3: { cellWidth: 18 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 },
        6: { cellWidth: 20 },
        7: { cellWidth: 16 },
      },
    tableWidth: pageWidth - margin * 2,
    theme: 'grid',
    headStyles: {
      fillColor: [46, 139, 87],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: hasManyColumns ? 1.2 : 2
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    bodyStyles: {
      textColor: [33, 37, 41]
    },
    margin: { top: 2, bottom: 14, left: margin, right: margin },
    horizontalPageBreak: hasManyColumns,
    horizontalPageBreakRepeat: 0,
    showHead: 'everyPage'
  });

  const totalPages = doc.getNumberOfPages();
  for (let pageNo = 1; pageNo <= totalPages; pageNo++) {
    doc.setPage(pageNo);
    drawPageBranding(pageNo, totalPages);
  }

  doc.save(fileName + '.pdf');
  }
  public exportMultiSheetExcel(jsonData: any, fileName: string): void {
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const jsonKeys = Object.keys(jsonData);
    if (jsonKeys && jsonKeys.length) {
      for (let key in jsonData) {
        const sheetData = Array.isArray(jsonData[key])
          ? jsonData[key].map((row: any) => this.sanitizeObjectForExcel(row))
          : [];
        const sheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, sheet, key);
      }
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      this.saveExcelFile(excelBuffer, fileName);
    }
  }

  private saveExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {type: this.fileType});
    FileSaver.saveAs(data, fileName + this.fileExtension);
  }

  private stripHtmlTags(html: string): string {
    if (html === null || html === undefined) {
      return '';
    }
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }

  private sanitizeExcelValue(value: any): any {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return this.stripHtmlTags(value);
    }

    if (value instanceof Date) {
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          const sanitizedItem = this.sanitizeExcelValue(item);
          return typeof sanitizedItem === 'object'
            ? JSON.stringify(sanitizedItem)
            : sanitizedItem;
        })
        .filter((item) => item !== '')
        .join(', ');
    }

    if (typeof value === 'object') {
      return this.sanitizeObjectForExcel(value);
    }

    return value;
  }

  private sanitizeObjectForExcel(value: Record<string, any>): Record<string, any> {
    if (!value || typeof value !== 'object' || value instanceof Date) {
      return value;
    }

    return Object.keys(value).reduce((acc: Record<string, any>, key) => {
      acc[key] = this.sanitizeExcelValue(value[key]);
      return acc;
    }, {});
  }

  private normalizeExportColumns(columns?: string[]): string[] {
    if (!Array.isArray(columns)) {
      return [];
    }

    return columns
      .filter((column): column is string => typeof column === 'string')
      .map(column => column.trim())
      .filter(column => column.length > 0)
      .filter(column => !['select', 'actions'].includes(column.toLowerCase()));
  }

  private projectRowForExport(
    item: any,
    columns: string[],
    index: number,
    courseMap?: Map<number, string>
  ): Record<string, any> {
    const row: Record<string, any> = {};

    columns.forEach((column) => {
      row[column] = this.resolveQuestionExportValue(item, column, index, courseMap);
    });

    return row;
  }

  private buildDynamicPdfColumnStyles(columns: string[], pageWidth: number, margin: number): Record<number, { cellWidth: number | 'wrap' }> {
  const availableWidth = pageWidth - margin * 2;
    const baseWidth = Math.max(12, Math.floor(availableWidth / Math.max(columns.length, 1)));

    return columns.reduce((styles, column, index) => {
      const normalizedColumn = column.trim().toLowerCase();
      let cellWidth = baseWidth;

      if (normalizedColumn === 's.no.' || normalizedColumn === 's.no' || normalizedColumn === 'sno') {
        cellWidth = 10;
      } else if (normalizedColumn === 'question text') {
        cellWidth = Math.max(35, Math.min(65, Math.floor(availableWidth * 0.28)));
      } else if (normalizedColumn === 'labels') {
        cellWidth = Math.max(30, Math.min(55, Math.floor(availableWidth * 0.2)));
      } else if (normalizedColumn === 'answer explanation') {
        cellWidth = Math.max(35, Math.min(60, Math.floor(availableWidth * 0.22)));
      } else if (normalizedColumn === 'course/job role') {
        cellWidth = Math.max(22, Math.min(35, Math.floor(availableWidth * 0.14)));
      } else if (
        normalizedColumn === 'created date' ||
        normalizedColumn === 'modified date' ||
        normalizedColumn === 'created datetime' ||
        normalizedColumn === 'modified datetime'
      ) {
        cellWidth = 25;
      } else if (
        normalizedColumn === 'language' ||
        normalizedColumn === 'language name' ||
        normalizedColumn === 'version' ||
        normalizedColumn === 'status' ||
        normalizedColumn === 'type' ||
        normalizedColumn === 'difficulty level'
      ) {
        cellWidth = Math.max(14, Math.min(26, Math.floor(availableWidth * 0.12)));
      }

      styles[index] = { cellWidth };
      return styles;
    }, {} as Record<number, { cellWidth: number | 'wrap' }>);
  }

  private resolveQuestionExportValue(
    item: any,
    column: string,
    index: number,
    courseMap?: Map<number, string>
  ): any {
    const normalizedColumn = column.trim().toLowerCase();

    const existingValue = this.resolveRawValue(item, column);
    if (existingValue !== null && existingValue !== undefined) {
      if (typeof existingValue === 'string') {
        return this.stripHtmlTags(existingValue);
      }
      return existingValue;
    }

    if (normalizedColumn === 's.no.' || normalizedColumn === 's.no' || normalizedColumn === 'sno') {
      return index + 1;
    }

    switch (normalizedColumn) {
      case 'question text':
        return this.stripHtmlTags(item?.question_text) || '';
      case 'course/job role':
        return item?.course_name || courseMap?.get(item?.course_id) || '';
      case 'version':
        return item?.version || '';
      case 'evaluation mode':
        return item?.question_category ?? item?.evaluation_mode ?? '';
      case 'type':
        return item?.question_type_name || '';
      case 'difficulty level':
        return item?.difficulty_level_name || '';
      case 'answer explanation':
        return this.stripHtmlTags(item?.answer_explanation || '') || '';
      case 'status':
        return this.resolveStatusValue(item);
      case 'labels':
        return this.formatQuestionLabels(item);
      case 'language':
      case 'language name':
        return item?.language_name || '';
      case 'created date':
      case 'created datetime':
        return this.formatDateValue(item?.created_datetime);
      case 'modified date':
      case 'modified datetime':
        return this.formatDateValue(item?.modified_datetime);
      case 'created user':
      case 'created user username':
        return item?.created_user_username || item?.created_user || '';
      case 'modified user':
      case 'modified user username':
        return item?.modified_user_username || item?.modified_user || '';
      case 'organization':
      case 'organization name':
        return item?.organization_name || '';
      case 'attachments':
        return item?.attachments || '';
      default: {
        return '';
      }
    }
  }

  private resolveRawValue(item: any, column: string): any {
    const directKey = column;
    if (item && Object.prototype.hasOwnProperty.call(item, directKey)) {
      return item[directKey];
    }

    const normalizedKey = column
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();

    if (item && Object.prototype.hasOwnProperty.call(item, normalizedKey)) {
      return item[normalizedKey];
    }

    return item?.[column] ?? item?.[normalizedKey];
  }

  private resolveStatusValue(item: any): string {
    const statusValue = item?.is_active ?? item?.status;

    if (typeof statusValue === 'string') {
      const normalizedStatus = statusValue.trim().toLowerCase();
      if (['true', '1', 'active'].includes(normalizedStatus)) {
        return 'Active';
      }
      if (['false', '0', 'inactive'].includes(normalizedStatus)) {
        return 'Inactive';
      }
    }

    if (typeof statusValue === 'number') {
      return statusValue === 1 ? 'Active' : 'Inactive';
    }

    return statusValue ? 'Active' : 'Inactive';
  }

  private formatQuestionLabels(item: any): string {
    if (Array.isArray(item?.labelGroups) && item.labelGroups.length > 0) {
      return item.labelGroups
        .map((group: any) => {
          const root = group?.rootLabel ?? '';
          const children = Array.isArray(group?.childLabels) ? group.childLabels.filter(Boolean) : [];
          if (root && children.length > 0) {
            return `${root}: ${children.join(', ')}`;
          }
          return root || children.join(', ');
        })
        .filter((value: string) => value && value.trim() !== '')
        .join(' | ');
    }

    if (!Array.isArray(item?.labels) || item.labels.length === 0) {
      return '';
    }

    return item.labels
      .map((group: any) => {
        if (!group || !Array.isArray(group.labels)) {
          return '';
        }

        const path = group.labels
          .map((label: any) => label?.labelName ?? label?.label_name ?? '')
          .filter((labelName: string) => labelName && labelName.trim() !== '');

        return path.join(' > ');
      })
      .filter((value: string) => value && value.trim() !== '')
      .join(' | ');
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return this.stripHtmlTags(String(value));
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
  

  // Helper function to process Questions data
  private processQuestionsForExcel(data: any[]): any[] {
    const columnOrder = [
      'S.No',
      'Question Text',
      'Course/Job Role', 
      'Version', 
      'Evaluation Mode',
      'Type',
      'Difficulty Level',
      'Created Datetime',
      'Modified Datetime',
      'Answer Explanation',
      'Language Name',
      'Attachments',
      'Status',   
      'Labels',
    ];

    const columnMapping: Record<string, string> = {
      question_text: 'Question Text',
      course_name: 'Course/Job Role',
      version: 'Version',
      question_category: 'Evaluation Mode',
      question_type_name: 'Type',
      difficulty_level_name: 'Difficulty Level',
      created_datetime: 'Created Datetime',
      modified_datetime: 'Modified Datetime',
      answer_explanation: 'Answer Explanation',
      language_name: 'Language Name',
      attachments: 'Attachments',
      is_active: 'Status',
      labels: 'Labels',
    };

    const excludeFields = [
      'question_id',
      'question_type_id',
      'difficulty_level_id',
      'language_id',
      'default_language_question_id',
      'subjective_answer_id',
      'language_count',
      'fill_up_id',
      'show_link',
      'processed_question_text',
      'version_id',
      'created_user',
      'created_user_username',
      'modified_user',
    ];

    const stripHtml = (html: string) => html ? html.replace(/<[^>]*>/g, '') : '';

    // Sort by course_id
    data.sort((a, b) => (a.course_id || 0) - (b.course_id || 0));

    const questionIdToSno: Record<number, number> = {};
    let snoCounter = 1;
    const result: any[] = [];

    for (const item of data) {
      let sno: number;

      if (item.parent_id && item.parent_id !== -1) {
        sno = questionIdToSno[item.parent_id];
      } else {
        sno = snoCounter++;
        questionIdToSno[item.question_id] = sno;
      }

      const filteredItem: any = { 'S.No': sno };

      for (const key of Object.keys(item)) {
        if (!excludeFields.includes(key)) {
          let value = item[key];

          // Remove HTML from relevant fields using your helper
          if ([
            'question_text',
            'answer_explanation',
            'actual_statement_text',
            'question_hint',
            'question_remarks',
            'question_feedback',
            'additional_information'
          ].includes(key)) {
            value = this.stripHtmlTags(value);
          }

          const newKey = columnMapping[key];
          if (newKey) {
            if (key === 'is_active') {
              filteredItem[newKey] = value ? 'Active' : 'Inactive';
            } else if (key === 'created_datetime' || key === 'modified_datetime') {
              filteredItem[newKey] = this.formatDateValue(value);
            } else if (key === 'labels') {
              filteredItem[newKey] = this.formatQuestionLabels(item);
            } else {
              filteredItem[newKey] = value;
            }
          }
        }
      }

      // Ensure all columns in order
      const orderedItem: any = {};
      columnOrder.forEach(col => {
        orderedItem[col] = filteredItem[col] !== undefined ? filteredItem[col] : '';
      });

      result.push(orderedItem);
    }

    return result;
  }

}

